import * as _ from 'underscore';
import { util } from '../util';
import { Inject, Injectable } from '@angular/core';
import { StateService, TransitionPromise, TransitionOptions } from '@uirouter/angular';
import { IExtendedWindow, IObject } from '@type_definitions/datatypes';
import { GaWebStorageService } from './storage/ga-web-storage.service';

export enum TranslationCategory {
  GA = 'ga',
  APP = 'app',
  BPPROFILES = 'bpprofiles',
  BPWIZARD = 'bpwizard',
  CATALOG = 'catalog',
  CREDIT_CARD = 'creditcard',
  DASHBOARD = 'dashboard',
  ORDERENTRY = 'orderentry',
  WEBSHOP = 'webshop',
  CHART = 'chart',
  REPORTS = 'reports',
  INVOICE = 'invoice'
}

/**
 * @ngdoc service
 * @name ga2.shared.services:stateService
 *
 * @description Use this service for ui-router state related stuff
 *
 */
@Injectable()
export class GaStateService {
  private _screen:string;
  
  constructor(
    @Inject(Window) private window:IExtendedWindow,
    private stateService:StateService,
    private webStorage:GaWebStorageService
  ) {
    //empty
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services:stateService#changeState
   * @methodOf ga2.shared.services:stateService
   *
   * @description jump to a different state (route) while preserving the current state params.
   *              As a side effect, the UI will scroll to 'content-top'
   *
   * @param {string} stateName the state (route) to jump to
   * @param {Object=} newParams new state params that should be injected in the target state
   * @param {TransitionOptions=} options new state params that should be injected in the target state
   */
  public changeState(stateName:string, newParams?:{}, options?:TransitionOptions):void {
    const stateParams = util.copyAndExtend(this.stateService.params || {}, newParams);
    this.stateService.go(stateName, stateParams, options);
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services:stateService#createSREF
   * @methodOf ga2.shared.services:stateService
   *
   * @description Creates an 'SREF' String that can be used in the ui-sref directive.
   *
   * @param {String} state the name of the target state
   * @param {Object=} stateParams the params of the target state
   *
   * @return {String} stateService.createSREF SREF String
   */
  public createSREF(state:string, stateParams?:IObject):string {
    let sref = state;
    if (stateParams) {
      sref += '({';
      const params = [];
      _.forEach(stateParams, (value:string | string[], key:string) => {
        if (util.notNullOrUndefined(value) && key !== '#') {
          if (_.isString(value)) {
            //surround single value with quotes
            value = '\'' + value + '\'';
          } else if (_.isArray(value)) {
            const values = <string[]>util.copyAndExtend(value);
            //surround individual values with quotes
            for (let i = 0; i < values.length; i++) {
              if (_.isString(values[ i ])) {
                values[ i ] = '\'' + values[ i ] + '\'';
              }
            }
            value = '[' + values + ']';
          }
          params.push(key + ':' + value);
        }
      });
      sref += params.join(',');
      sref += '})';
    }

    return sref;
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services:stateService#getCurrentTranslationCategory
   * @methodOf ga2.shared.services:stateService
   *
   * @description This uses the first segment from the current URL to check if it is a valid category
   *
   * @return {String} If there is a match, it returns the category name; otherwise it returns the default category: app
   */
  public get currentTranslationCategory():TranslationCategory {
    const currentCategory:TranslationCategory = this.checkScreenAlias(<TranslationCategory>this.getPathSegment(0));
    return this.getTranslationCategoriesExcludingGA.indexOf(currentCategory) >= 0? currentCategory : <TranslationCategory>TranslationCategory.APP;
  }

  // allows overwriting the screen when it is open inside a dialog loaded in a different screen (e.g. webshop info loaded in browselist/dashboard)
  public set screen(name:string) {
    this._screen = name;
  }

  public get screen():string {
    return this._screen || this.getCurrentScreen();
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services:stateService#getCurrentScreen
   * @methodOf ga2.shared.services:stateService
   *
   * @description Get the current screen is composed of current category and current view
   *
   * @return {String} A screen name
   */
  private getCurrentScreen():string {
    const category:TranslationCategory = this.currentTranslationCategory;

    const view:string = this.currentView;
    if (view) {
      return `${category}.${view}`;
    }

    return category;
  }

  public get currentView():string {
    const view:string | number = this.getPathSegment('last');

    // when view has a numeric value
    if (!isNaN(<number>view)) {
      return undefined;
    }

    return (<string>view).replace('-', '_'); // convert to underscore
  }

  private checkScreenAlias(value:TranslationCategory):TranslationCategory {
    const allowedAliases:IObject<TranslationCategory> = {
      'lists': TranslationCategory.REPORTS
    };

    // if alias is not found then just return the category as it is
    return allowedAliases[value] || value;
  }

  private getPathSegment(segment:number | string, isAbsolute?:boolean):string | number {
    let pathURL:string = isAbsolute? this.window.location.href : this.window.location.hash.substr(1, this.window.location.hash.length);
    if (pathURL.includes('?')) {
      pathURL = pathURL.split('?')[0];
    }
    const path:string[] = pathURL.substr(1).split('/');
    return path && ((segment === 'last')? path[ path.length - 1 ] : path[ segment ]);
  }

  // update a query parameter
  public updateStateParameter(parameterName:string, newValue:any):TransitionPromise {
    const newParams = util.copyAndExtend(this.stateService.params);
    if (util.isNullOrUndefined(newValue)) {
      delete newParams[ parameterName ];
    } else {
      newParams[ parameterName ] = newValue;
    }

    return this.stateService.go('.', newParams, { inherit: false });
  }

  public fullReload():void {
    const RELOADED:string = 'ga2.reloadTime';
    const reloadedTime:number = this.webStorage.session.get(RELOADED);
    const currentTime:number = new Date().getTime();
    if (!reloadedTime || currentTime - reloadedTime >= 5000) { // prevent reloading page in a loop
      this.webStorage.session.set(RELOADED, currentTime);
      this.window.location.reload();
    }
  }

  public getGAVersion():string {
    const gaVersion:string = <string>this.getPathSegment(3, true);
    return /^ga(\d)$/.test(gaVersion)? gaVersion : 'ga2';
  }

  private get getTranslationCategoriesExcludingGA():TranslationCategory[] {
    return _.chain(TranslationCategory)
      .values()
      .without(TranslationCategory.GA)
      .value();
  }

  public clear():void {
    delete this._screen;
  }
}